import { Library, WebToolsConfig } from './types';
import { rollbar } from './rollbar';
import defaultConfig from './config.json';

const isLoaded = (name: string): boolean => !!document.querySelector(`script[data-name="${name}"]`);

const getConfig = (): WebToolsConfig => {
  const userCfg = window.webToolsConfig ?? {};
  const userLibs = userCfg.libraries ?? [];
  const defaultLibs = defaultConfig.libraries ?? [];
  const libraries = userCfg.mode === 'override' ? userLibs : [...defaultLibs, ...userLibs];

  return { ...defaultConfig, ...userCfg, libraries };
};

const createScript = ({ name, attrs = {} }: Library): HTMLScriptElement => {
  const { src, textContent, defer, async, ...rest } = attrs;
  const el = document.createElement('script');
  el.dataset.name = name;
  if (textContent) el.textContent = textContent;
  if (src) el.src = src;
  if (defer) el.defer = true;
  if (async) el.async = true;
  Object.entries(rest).forEach(([k, v]) => v && el.setAttribute(k, String(v)));

  return el;
};

const load = ({ name, attrs }: Library): Promise<void> => {
  if (isLoaded(name)) return Promise.resolve();

  return new Promise<void>((resolve, reject) => {
    try {
      const script = createScript({ name, attrs });

      script.addEventListener('error', reject);
      script.addEventListener('load', () => {
        dispatchEvent(new Event(`web-tools:loaded:${name}`));
        resolve();
      });

      document.head.appendChild(script);
    } catch (e) {
      rollbar.error(e as Error);
      reject(e);
    }
  });
};

const loadAll = async (): Promise<void> => {
  await Promise.allSettled((getConfig().libraries ?? []).map(load));
};

(() => {
  const config = getConfig();
  const ready = config.autoload === false ? Promise.resolve() : loadAll(); // loads by default
  window.webTools = {
    config,
    isLoaded,
    load,
    loadAll,
    ready,
    rollbar,
    version: APP_VERSION
  };
  ready.then(() => dispatchEvent(new Event('web-tools:ready')));
})();
